import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout, { usePageTitle } from '../components/site/Layout';
import VehicleSearch from '../components/site/VehicleSearch';
import { loadVehicle } from '../lib/nhtsa';

const n = (v) => (v === null || v === undefined ? null : v);

// better: 'low' = the smaller number wins, 'high' = the bigger number wins
const ROWS = [
  { label: 'Recall campaigns', better: 'low', get: (d) => d.recalls.length },
  { label: 'Recalls that say "do not drive"', better: 'low', get: (d) => d.recalls.filter((r) => r.doNotDrive).length },
  { label: 'Owner complaints', better: 'low', get: (d) => n(d.complaints && d.complaints.count), fmt: (v) => v.toLocaleString('en-US') },
  { label: 'Complaints involving a crash or fire', better: 'low', get: (d) => (d.complaints ? d.complaints.crash + d.complaints.fire : null) },
  { label: 'Injuries reported', better: 'low', get: (d) => n(d.complaints && d.complaints.injuries) },
  { label: 'Overall crash rating', better: 'high', get: (d) => n(d.safety && d.safety.overall), fmt: (v) => `${v} / 5 stars` },
  { label: 'Front crash rating', better: 'high', get: (d) => n(d.safety && d.safety.front), fmt: (v) => `${v} / 5` },
  { label: 'Side crash rating', better: 'high', get: (d) => n(d.safety && d.safety.side), fmt: (v) => `${v} / 5` },
  { label: 'Rollover rating', better: 'high', get: (d) => n(d.safety && d.safety.rollover), fmt: (v) => `${v} / 5` },
];

function Side({ label, side, onRun }) {
  return (
    <div className="cv-vs-side">
      <h3>{label}</h3>
      <VehicleSearch compact onSubmit={onRun} loading={side.status === 'loading'} error={side.error} />
      {side.status === 'done' && <div className="cv-vs-loaded">✓ {side.data.title}</div>}
    </div>
  );
}

export default function CompareCars() {
  usePageTitle('Compare Two Cars — Recalls, Complaints & Crash Ratings Side by Side | CompareVIN');
  const [a, setA] = useState({ status: 'idle', data: null, error: '' });
  const [b, setB] = useState({ status: 'idle', data: null, error: '' });
  const ids = useRef({ a: 0, b: 0 });

  const runner = (key, set) => async (input) => {
    const id = ++ids.current[key];
    set({ status: 'loading', data: null, error: '' });
    try {
      const data = await loadVehicle(input);
      if (id === ids.current[key]) set({ status: 'done', data, error: '' });
    } catch (e) {
      if (id !== ids.current[key]) return;
      const known = e && e.message && !/HTTP|Failed to fetch|NetworkError/i.test(e.message);
      set({ status: 'error', data: null, error: known ? e.message : "Couldn't reach NHTSA just now. Please try again." });
    }
  };

  const ready = a.status === 'done' && b.status === 'done';
  let table = null;
  if (ready) {
    let winsA = 0, winsB = 0, decisive = 0;
    const rows = ROWS.map((r) => {
      const va = r.get(a.data), vb = r.get(b.data);
      let win = null;
      if (va !== null && vb !== null && va !== vb) {
        win = (r.better === 'low') === (va < vb) ? 'a' : 'b';
        decisive += 1;
        if (win === 'a') winsA += 1; else winsB += 1;
      }
      return { ...r, va, vb, win };
    }).filter((r) => r.va !== null || r.vb !== null);

    let verdict;
    if (decisive === 0) verdict = 'Too close to call on the public safety data.';
    else if (winsA === winsB) verdict = `A dead heat: each car comes out ahead on ${winsA} measure${winsA === 1 ? '' : 's'}.`;
    else {
      const [name, w] = winsA > winsB ? [a.data.title, winsA] : [b.data.title, winsB];
      verdict = `${name} comes out ahead on ${w} of ${decisive} measures.`;
    }
    const show = (v, fmt) => (v === null ? '—' : fmt ? fmt(v) : v);

    table = (
      <>
        <div className="cv-verdict">
          <h3>{verdict}</h3>
          <p>Read this as a safety-data tiebreaker, not a verdict on the car. Complaint and recall counts grow with how many were sold and how long they have been on the road, so an older or higher-volume model year will naturally look worse. Neither result says anything about accidents or ownership history.</p>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="cv-cmp">
            <thead><tr><th /><th>{a.data.title}</th><th>{b.data.title}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label}>
                  <th scope="row">{r.label}</th>
                  <td className={r.win === 'a' ? 'win' : ''}>{show(r.va, r.fmt)}</td>
                  <td className={r.win === 'b' ? 'win' : ''}>{show(r.vb, r.fmt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="cv-next">
          <div>
            <h3>Down to one car?</h3>
            <p>Don't pay for a history report on both. Run it on the finalist — we'll tell you which one fits.</p>
          </div>
          <Link className="cv-btn" to="/#quiz">Find the right report →</Link>
        </div>
      </>
    );
  }

  return (
    <Layout>
      <section className="cv-hero">
        <div className="cv-wrap">
          <div className="cv-eyebrow">Shortlist tool</div>
          <h1>Compare two cars before you spend a cent.</h1>
          <p className="cv-lead">Torn between two cars — or two model years of the same one? Put them side by side on recalls, owner complaints and official crash ratings.</p>
        </div>
      </section>

      <section className="cv-section" style={{ paddingTop: 40 }}>
        <div className="cv-wrap">
          <div className="cv-vs" style={{ marginTop: 0 }}>
            <Side label="Car A" side={a} onRun={runner('a', setA)} />
            <Side label="Car B" side={b} onRun={runner('b', setB)} />
          </div>
          {!ready && (
            <p className="cv-muted" style={{ marginTop: 22 }}>Enter a VIN or a year, make and model for each car. Both results appear here as soon as they load.</p>
          )}
          {table}

          <div className="cv-usecases">
            {[
              ['Two different models', 'Civic or Corolla? See which one has the cleaner safety record for the year you can afford.'],
              ['Two model years of one car', '2017 or 2019? Spot the year with fewer recalls and complaints before you pay the premium.'],
              ['Two listings, one model', 'Enter each car\'s VIN to confirm what you are really looking at, and catch a VIN that decodes to something else.'],
            ].map(([t, d]) => (
              <div className="cv-criterion" key={t}><h4>{t}</h4><p>{d}</p></div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
